import React, { useState, useMemo } from "react";
import styles from "./paymentDetails.module.css";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { planIdSelector } from "../../../../services/selectors/entitlements.selectors";
import { useGetCardLast4DigitsQuery, useCancelSubscriptionMutation, useChangeSubscriptionCardMutation, useGetPaymentsByCompanyIdQuery, useGetAllPlansQuery } from "../../../../services/api/utils.api";
import OnboardingPaymentStep from "../../../../pages/onboarding/onboardingPaymentStep";
import Loader from "../../../loader/loader";
import Modal from "../../modal/Modal";
import DeleteConfirmation from "../../deleteConfirmation/deleteConfirmation";
import GeneralSuccessPopup from "../../generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../errorPopup/errorPopup";
import i18n from "../../../../i18n";

const CardIcon = () => (
  <svg className={styles.cardIcon} viewBox="0 0 24 24" fill="none">
    <path
      d="M2 8.5H22M6 15.5H8M10.5 15.5H12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M2 8.5C2 7.397 2.897 6.5 4 6.5H20C21.103 6.5 22 7.397 22 8.5V16.5C22 17.603 21.103 18.5 20 18.5H4C2.897 18.5 2 17.603 2 16.5V8.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none">
    <path
      d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg className={styles.lockIcon} viewBox="0 0 24 24" fill="none">
    <path
      d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg className={styles.userIcon} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M4 21C4 17 8 15 12 15C16 15 20 17 20 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// Types
interface CreditCardForm {
  fullName: string;
  cardOwnerId: string;
  cardNumber: string;
  expiry: string;
  cvv: string;
}

const formatCardNumber = (value: string): string => {
  return value
    .replace(/\D/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim()
    .slice(0, 19);
};

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

const INITIAL_FORM_STATE: CreditCardForm = {
  fullName: "",
  cardOwnerId: "",
  cardNumber: "",
  expiry: "",
  cvv: "",
};

interface PaymentDetailsTabProps {
  /** Lets the "no subscription yet" state send the user to the tab that can actually take a card. */
  onNavigateToTab?: (tabId: string) => void;
}

export const PaymentDetailsTab: React.FC<PaymentDetailsTabProps> = ({ onNavigateToTab }) => {
  const admin = useAppSelector(adminSelector);
  const entitlementsPlanId = useAppSelector(planIdSelector);

  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRemoveSuccess, setShowRemoveSuccess] = useState(false);
  const [showRemoveError, setShowRemoveError] = useState(false);
  const [removeErrorMessage, setRemoveErrorMessage] = useState<string>("");
  const [showUpdateError, setShowUpdateError] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string>("");
  const [cardForm, setCardForm] = useState<CreditCardForm>(INITIAL_FORM_STATE);
  const [showRemoveCardConfirmation, setShowRemoveCardConfirmation] = useState(false);

  const { data, isLoading, refetch } = useGetCardLast4DigitsQuery(admin.companyId);
  const { data: paymentsResponse, isLoading: isPaymentsLoading, refetch: refetchPayments } = useGetPaymentsByCompanyIdQuery(
    { type: 1, companyId: admin.companyId },
    { skip: !admin.companyId }
  );
  const { data: plans = [], isLoading: isPlansLoading } = useGetAllPlansQuery();
  const [cancelSubscription, { isLoading: isRemoving }] = useCancelSubscriptionMutation();
  const [changeSubscriptionCard, { isLoading: isUpdating }] = useChangeSubscriptionCardMutation();

  const cardLast4Digits = data?.cardLast4Digits || null;
  const displayMessage = cardLast4Digits ? t("CURRENT_PAYMENT_METHOD") : (data?.message || t("NO_CARD_ON_FILE"));
  
  const activePayment = useMemo(() =>
    paymentsResponse?.data?.find((payment) =>
      payment.statusId === 1 && payment.paymentUid !== null
    ) ?? null,
    [paymentsResponse]
  );

  const hasActiveSubscription = activePayment !== null;

  const currentPlan = useMemo(() =>
    plans.find(plan => plan.id === (activePayment?.plansId ?? entitlementsPlanId)) ?? null,
    [plans, activePayment, entitlementsPlanId]
  );

  // Whether a company can hold a card is a question about its PLAN's price, not about whether a
  // payment row happens to exist right now: the server only creates a subscription — and only then
  // stores a card — when the plan actually costs something. A paid company whose card was removed
  // has no payment row either, and it must still be able to enter one.
  const { monthlyPrice, yearlyPrice } = useMemo(() => {
    const price = currentPlan?.prices?.find(p => (p.monthlyPrice ?? 0) > 0 || (p.yearlyPrice ?? 0) > 0);
    return {
      monthlyPrice: price?.monthlyPrice ?? currentPlan?.monthlyPrice ?? 0,
      yearlyPrice: price?.yearlyPrice ?? currentPlan?.yearlyPrice ?? 0,
    };
  }, [currentPlan]);

  const isPaidPlan = monthlyPrice > 0 || yearlyPrice > 0;

  // Paid plan with no active recurring payment: there is no subscription to update the card on
  // (changeSubscriptionCard / CompanyPayment/22 would answer "payment recuring not exist"), so the
  // card has to be entered through the "add" endpoint (type=1), which creates the subscription and
  // stores the card together. That is exactly what OnboardingPaymentStep does.
  const needsNewSubscription = !hasActiveSubscription && isPaidPlan;
  // Charge on the period the plan actually has a price for; monthly when both are priced.
  const newSubscriptionPeriodTypeId = monthlyPrice > 0 ? 1 : 2;

  // Only a genuinely free plan is left without any way to hold a card.
  const canManageCard = hasActiveSubscription || needsNewSubscription;

  const updateFormField = (field: keyof CreditCardForm, value: string) => {
    setCardForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateCard = async () => {
    if (!activePayment) {
      setUpdateErrorMessage(t("CARD_REQUIRES_ACTIVE_SUBSCRIPTION"));
      setShowUpdateError(true);
      return;
    }

    if (!activePayment.plansId || !activePayment.paymentsPeriodTypesId) {
      console.error("Active payment %s is missing plan or payment period information", activePayment.id);
      setUpdateErrorMessage(t("SUBSCRIPTION_MISSING_PLAN_INFO"));
      setShowUpdateError(true);
      return;
    }

    try {
      const expiryMMyy = formatExpiry(cardForm.expiry);

      await changeSubscriptionCard({
        plansId: activePayment.plansId,
        paymentsPeriodTypesId: activePayment.paymentsPeriodTypesId,
        companyId: admin.companyId,
        slikaObject: {
          cardOwnerName: cardForm.fullName,
          cardOwnerTaz: cardForm.cardOwnerId,
          cardNumber: cardForm.cardNumber.replace(/\s/g, ""),
          cardDateMMyy: expiryMMyy,
          cardCvv: cardForm.cvv,
        },
      }).unwrap();

      setShowSuccess(true);
      setIsEditMode(false);
      setCardForm(INITIAL_FORM_STATE);
      
      refetch();
      
    } catch (error: any) {
      console.error("Failed to update card:", error);
      
      let errorMessage = t("CARD_UPDATE_ERROR");
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setUpdateErrorMessage(errorMessage);
      setShowUpdateError(true);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setCardForm(INITIAL_FORM_STATE);
  };

  // The "add" endpoint created the subscription and stored the card, so both the card display and
  // the payments row it is derived from have to be re-read.
  const handleNewSubscriptionSuccess = () => {
    setIsEditMode(false);
    setShowSuccess(true);
    refetch();
    refetchPayments();
  };

  const handleRemoveCard = async () => {
    try {
      if (!admin.companyId) return;
      await cancelSubscription({ companyId: admin.companyId }).unwrap();
      setShowRemoveCardConfirmation(false);
      setShowRemoveSuccess(true);
      
      // Refetch card data to update the display
      refetch();
      
    } catch (error: any) {
      console.error("Failed to remove card:", error);
      setShowRemoveCardConfirmation(false);
      setRemoveErrorMessage(error?.data?.message || error?.message || t("CARD_REMOVAL_ERROR"));
      setShowRemoveError(true);
    }
  };

  if (isLoading || isPaymentsLoading || isPlansLoading) {
    return (
      <div className={styles.paymentDetails}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.paymentDetails}>
      {!isEditMode ? (
        <CardDisplay
          displayMessage={displayMessage}
          cardLast4Digits={cardLast4Digits}
          canManageCard={canManageCard}
          hasActiveSubscription={hasActiveSubscription}
          onUpdateClick={() => setIsEditMode(true)}
          onRemoveCardClick={() => setShowRemoveCardConfirmation(true)}
          onGoToSubscription={onNavigateToTab ? () => onNavigateToTab("subscription") : undefined}
        />
      ) : needsNewSubscription && currentPlan ? (
        <div className={styles.newSubscriptionForm}>
          <OnboardingPaymentStep
            companyId={admin.companyId}
            planId={currentPlan.id}
            paymentPeriodTypeId={newSubscriptionPeriodTypeId}
            contactFullName={cardForm.fullName || undefined}
            onPaymentSuccess={handleNewSubscriptionSuccess}
          />
          <button className={styles.cancelButton} onClick={handleCancel}>
            {t("CANCEL")}
          </button>
        </div>
      ) : (
        <CardForm
          cardForm={cardForm}
          onFieldChange={updateFormField}
          onSave={handleUpdateCard}
          onCancel={handleCancel}
          isLoading={isUpdating}
        />
      )}
      
      {showSuccess && (
        <Modal onClose={() => setShowSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => setShowSuccess(false)}
          />
        </Modal>
      )}

      {showRemoveSuccess && (
        <Modal onClose={() => setShowRemoveSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => setShowRemoveSuccess(false)}
          />
        </Modal>
      )}

      {showRemoveError && (
        <Modal onClose={() => setShowRemoveError(false)}>
          <ErrorPopup
            onClose={() => setShowRemoveError(false)}
            text={removeErrorMessage}
          />
        </Modal>
      )}

      {showUpdateError && (
        <Modal onClose={() => setShowUpdateError(false)}>
          <ErrorPopup
            onClose={() => setShowUpdateError(false)}
            text={updateErrorMessage}
          />
        </Modal>
      )}

      {showRemoveCardConfirmation && (
        <Modal onClose={() => !isRemoving && setShowRemoveCardConfirmation(false)}>
          <DeleteConfirmation
            onClose={() => setShowRemoveCardConfirmation(false)}
            onDelete={handleRemoveCard}
            text={t("CONFIRM_REMOVE_CARD")}
          />
        </Modal>
      )}
    </div>
  );
};

// Sub-components
interface CardDisplayProps {
  displayMessage: string;
  cardLast4Digits: string | null;
  /** False only for a free plan, which the server cannot attach a card to at all. */
  canManageCard: boolean;
  hasActiveSubscription: boolean;
  onUpdateClick: () => void;
  onRemoveCardClick: () => void;
  onGoToSubscription?: () => void;
}

const CardDisplay: React.FC<CardDisplayProps> = ({
  displayMessage,
  cardLast4Digits,
  canManageCard,
  hasActiveSubscription,
  onUpdateClick,
  onRemoveCardClick,
  onGoToSubscription
}) => (
  <div className={styles.cardInfo}>
    <h4>{displayMessage}</h4>

    {cardLast4Digits && (
      <div className={styles.cardDisplay}>
        <div className={styles.cardIdentifier}>
          <span className={styles.cardNumber}>
            {cardLast4Digits} **** **** ****
          </span>
        </div>
      </div>
    )}

    {canManageCard ? (
      <>
        <button className={styles.updateButton} onClick={onUpdateClick}>
          {cardLast4Digits ? t("UPDATE_CARD") : t("ADD_CARD")}
        </button>

        {cardLast4Digits && hasActiveSubscription && (
          <button
            className={styles.removeCard}
            onClick={onRemoveCardClick}
          >
            {t("REMOVE_CARD")}
          </button>
        )}
      </>
    ) : (
      // Free plan: the server has nothing to attach a card to and stores none, so a card form here
      // could not save anything. Offer the flow that can — choosing a paid plan, which collects the
      // card as part of it.
      <>
        <p className={styles.noSubscriptionNote}>{t("CARD_REQUIRES_PAID_PLAN")}</p>
        {onGoToSubscription && (
          <button className={styles.updateButton} onClick={onGoToSubscription}>
            {t("GO_TO_SUBSCRIPTION")}
          </button>
        )}
      </>
    )}
  </div>
);

interface CardFormProps {
  cardForm: CreditCardForm;
  onFieldChange: (field: keyof CreditCardForm, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CardForm: React.FC<CardFormProps> = ({ 
  cardForm, 
  onFieldChange, 
  onSave, 
  onCancel,
  isLoading = false
}) => (
  <div className={styles.cardForm}>
    <FormField
      label={t("FULL_NAME_ON_CARD")}
      placeholder={t("ENTER_FULL_NAME")}
      value={cardForm.fullName}
      onChange={(value) => onFieldChange("fullName", value)}
      icon={<UserIcon />}
    />

    <FormField
      label={t("CARD_OWNER_ID")}
      placeholder={t("ENTER_ID_NUMBER")}
      value={cardForm.cardOwnerId}
      onChange={(value) => onFieldChange("cardOwnerId", value.replace(/\D/g, ""))}
      icon={<UserIcon />}
      maxLength={9}
    />

    <FormField
      label={t("CARD_NUMBER")}
      placeholder="0000 0000 0000 0000"
      value={cardForm.cardNumber}
      onChange={(value) => onFieldChange("cardNumber", formatCardNumber(value))}
      icon={<CardIcon />}
      maxLength={19}
      dir="ltr"
    />

    <div className={styles.formRow}>
      <FormField
        label={t("CARD_EXPIRY")}
        placeholder="MM/YY"
        value={cardForm.expiry}
        onChange={(value) => onFieldChange("expiry", formatExpiry(value))}
        icon={<CalendarIcon />}
        maxLength={5}
      />
      
      <FormField
        label={t("CARD_CVV")}
        placeholder="CVC"
        value={cardForm.cvv}
        onChange={(value) => onFieldChange("cvv", value.replace(/\D/g, ""))}
        icon={<LockIcon />}
        maxLength={4}
        tooltip={t("CARD_CVV_TOOLTIP")}
      />
    </div>

    <div className={styles.buttonGroup}>
      <button 
        className={styles.cancelButton} 
        onClick={onCancel}
        disabled={isLoading}
      >
        {t("CANCEL")}
      </button>
      <button 
        className={styles.saveButton} 
        onClick={onSave}
        disabled={isLoading}
      >
        {isLoading ? t("SAVING") : t("SAVE_NEW_CARD")}
      </button>
    </div>
  </div>
);

interface FormFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  icon: React.ReactNode;
  maxLength?: number;
  tooltip?: string;
  dir?: 'ltr' | 'rtl';
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  placeholder,
  value,
  onChange,
  icon,
  maxLength,
  tooltip,
  dir,
}) => (
  <div className={`${styles.formGroup} ${styles.hasIcon}`}>
    <label htmlFor={label}>
      {tooltip ? (
        <span className={styles.tooltip}>
          {label}
          <span className={styles.tooltipText}>{tooltip}</span>
        </span>
      ) : (
        label
      )}
    </label>
    <div className={styles.inputWrapper} dir={dir || i18n.dir()}>
      {icon}
      <input
        id={label}
        type="text"
        title={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
      />
    </div>
  </div>
);